/**
 * Universe tab — the merged explorer over what the trading agent can see:
 * current Alpaca positions, the owner-curated sector pool, the watchlist
 * artifact, and cached next-earnings dates. The provenance surface
 * (`pool_source` / `watchlist_source`: "live" vs "fallback (repo)") tells the
 * owner whether each set came from the live store or the repo fallback.
 *
 * MERGE CONTRACT (authored against reads.ts getUniverse + the agent shapes):
 *   - positions: the latest run artifact's top-level `positions[]` (reads.ts
 *     extractPositions). Each row carries `symbol`, plus best-effort `qty` and a
 *     value field (`market_value` | `price`). Positions are READ-ONLY facts.
 *   - pool: a PARSED array of `{symbol, sector}` (research.pool resolves via
 *     parseMaybeJson on the DDB JSON, or yaml.load on the repo fallback —
 *     either way reads.ts hands us an array). `pool_source` ∈ live|fallback|default.
 *   - watchlist: the RAW context.watchlist value. The store resolver returns
 *     EITHER a parsed object `{max_size, watchlist:[...]}` (repo-fallback path,
 *     yaml.load) OR a raw string (live DDB content that is YAML, since
 *     parseMaybeJson can't JSON.parse YAML) OR a JSON object. `parseWatchlist`
 *     tolerates all three and tolerates string entries ("just a symbol").
 *   - earnings: the RAW market-data DDB rows (reads.ts passes the ddbQuery
 *     result straight through — NOT the earnings_summary payload). Each row's
 *     shape (agent earnings.py docstring): `{ data_key:"<SYM>#earnings", symbol,
 *     kind:"earnings", next_earnings_date:"YYYY-MM-DD"|null, fetched_at, ... }`.
 *     The earnings DATE field is `next_earnings_date`; the symbol comes from
 *     `symbol` or is parsed off `data_key` (strip the "#earnings" suffix).
 *
 * Blackout: a row is flagged when its next-earnings date is within
 * `research.earnings_blackout_days` of `now` (inclusive, 0..N forward days).
 * The threshold comes from useSettings() row research.earnings_blackout_days,
 * falling back to the registry default via byKey.
 *
 * Styling is deferred to Task 8; only semantic classNames are emitted here.
 */

import { useMemo, useRef, useState } from 'react';
import SymbolTable from '../components/SymbolTable';
import UniverseMap from '../components/UniverseMap';
import { byKey } from '../lib/registry';
import { useAuth } from '../lib/auth';
import {
  useUniverse,
  useSettings,
  usePutPool,
  usePutContext,
  type UniverseResponse,
  type SettingRow,
} from '../lib/queries';
import { ApiError } from '../lib/api';

// ---------------------------------------------------------------------------
// Pure types + helpers (exported for unit tests — no DOM needed to exercise
// the merge math).
// ---------------------------------------------------------------------------

/** One merged row across positions / pool / watchlist / earnings. */
export interface UniverseRow {
  symbol: string;
  /** Pool sector, when the symbol is in the pool. */
  sector: string | null;
  inPosition: boolean;
  inPool: boolean;
  inWatchlist: boolean;
  qty: number | null;
  /** Latest position value (market_value, else price). */
  lastValue: number | null;
  /** Cached next-earnings date string (YYYY-MM-DD) or null. */
  nextEarnings: string | null;
  /** True when nextEarnings is within the blackout window of `now`. */
  earningsBlackout: boolean;
}

/** A pool entry as the API hands it back: `{symbol, sector}` (parsed). */
export interface PoolEntry {
  symbol: string;
  sector?: string | null;
}

/** A normalized watchlist, parsed from the tolerant input shapes. */
export interface ParsedWatchlist {
  maxSize: number | null;
  entries: Array<{ symbol: string; [k: string]: unknown }>;
}

function upper(s: unknown): string | null {
  if (typeof s !== 'string') return null;
  const t = s.trim().toUpperCase();
  return t.length > 0 ? t : null;
}

function asNumber(v: unknown): number | null {
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

/**
 * Tolerant watchlist parser. Accepts:
 *   - a parsed object `{max_size, watchlist:[...]}` (repo-fallback yaml.load),
 *   - a raw string that is JSON (`JSON.parse`), or YAML (minimal line scan),
 *   - entries that are objects `{symbol, ...}` OR bare strings ("NVDA").
 * Never throws; an unparseable input yields `{maxSize:null, entries:[]}`.
 *
 * NOTE on YAML at runtime: js-yaml is a devDependency only (not bundled at
 * runtime). The live-DDB path can hand us YAML *text*; rather than pull a YAML
 * lib into the bundle we do a minimal, shape-specific line scan for the known
 * watchlist artifact (a flat list of `- symbol: X` / `- X` under `watchlist:`
 * plus a top-level `max_size:`). This is intentionally narrow — see PR note.
 */
export function parseWatchlist(raw: unknown): ParsedWatchlist {
  let obj: unknown = raw;
  if (typeof raw === 'string') {
    const text = raw.trim();
    if (text === '') return { maxSize: null, entries: [] };
    try {
      obj = JSON.parse(text);
    } catch {
      return parseWatchlistYamlish(text);
    }
  }
  if (Array.isArray(obj)) {
    return { maxSize: null, entries: normalizeEntries(obj) };
  }
  if (obj && typeof obj === 'object') {
    const rec = obj as Record<string, unknown>;
    const list = Array.isArray(rec.watchlist) ? rec.watchlist : [];
    return { maxSize: asNumber(rec.max_size), entries: normalizeEntries(list) };
  }
  return { maxSize: null, entries: [] };
}

/** Coerce a raw watchlist list (objects or bare-string rows) to normalized
 *  entries, dropping rows with no resolvable symbol. */
function normalizeEntries(list: unknown[]): ParsedWatchlist['entries'] {
  const out: ParsedWatchlist['entries'] = [];
  for (const item of list) {
    if (typeof item === 'string') {
      const sym = upper(item);
      if (sym) out.push({ symbol: sym });
      continue;
    }
    if (item && typeof item === 'object') {
      const rec = item as Record<string, unknown>;
      const sym = upper(rec.symbol);
      if (sym) out.push({ ...rec, symbol: sym });
    }
  }
  return out;
}

/** Minimal YAML-ish scan for the known watchlist artifact shape. Reads a
 *  top-level `max_size:` and `- symbol: X` / `- X` items under `watchlist:`,
 *  plus each `- symbol:` entry's indented `rationale:` / `added:` lines so the
 *  promote round-trip (watchlistToYaml) preserves owner-authored metadata.
 *  Deliberately narrow; anything fancier should round-trip through the server. */
function parseWatchlistYamlish(text: string): ParsedWatchlist {
  let maxSize: number | null = null;
  const entries: ParsedWatchlist['entries'] = [];
  let current: { symbol: string; [k: string]: unknown } | null = null;
  let inList = false;
  for (const lineRaw of text.split(/\r?\n/)) {
    // Only strip whole-line comments (a `#` may legitimately appear inside a
    // quoted rationale value, so don't strip mid-line).
    if (/^\s*#/.test(lineRaw)) continue;
    const trimmed = lineRaw.trim();
    if (trimmed === '') continue;
    const maxMatch = /^max_size:\s*(\d+)/.exec(trimmed);
    if (maxMatch && !inList) {
      maxSize = Number(maxMatch[1]);
      continue;
    }
    if (/^watchlist:\s*$/.test(trimmed)) {
      inList = true;
      continue;
    }
    if (inList) {
      // `- symbol: NVDA`  or  `- NVDA`
      const objMatch = /^-\s*symbol:\s*(.+)$/.exec(trimmed);
      const bareMatch = /^-\s*["']?([A-Za-z0-9.\-]+)["']?\s*$/.exec(trimmed);
      if (objMatch) {
        const sym = upper(unquoteScalar(objMatch[1]));
        if (sym) {
          current = { symbol: sym };
          entries.push(current);
        } else {
          current = null;
        }
        continue;
      }
      if (bareMatch) {
        const sym = upper(bareMatch[1]);
        if (sym) {
          current = { symbol: sym };
          entries.push(current);
        } else {
          current = null;
        }
        continue;
      }
      // Indented `rationale:` / `added:` lines belonging to the current entry.
      if (current) {
        const ratMatch = /^rationale:\s*(.+)$/.exec(trimmed);
        if (ratMatch) {
          current.rationale = unquoteScalar(ratMatch[1]);
          continue;
        }
        const addedMatch = /^added:\s*(.+)$/.exec(trimmed);
        if (addedMatch) {
          current.added = unquoteScalar(addedMatch[1]);
        }
      }
    }
  }
  return { maxSize, entries };
}

/** Unwrap a single YAML scalar value: strips a matching pair of surrounding
 *  single or double quotes, undoubling internal single quotes (the YAML
 *  single-quote escape). Leaves unquoted scalars untouched. */
function unquoteScalar(raw: string): string {
  const v = raw.trim();
  if (v.length >= 2 && v[0] === '"' && v[v.length - 1] === '"') {
    return v.slice(1, -1);
  }
  if (v.length >= 2 && v[0] === "'" && v[v.length - 1] === "'") {
    return v.slice(1, -1).replace(/''/g, "'");
  }
  return v;
}

/** A position row as best-effort extracted from the run artifact. */
function positionSymbol(p: unknown): string | null {
  if (!p || typeof p !== 'object') return null;
  return upper((p as Record<string, unknown>).symbol);
}

function positionQty(p: Record<string, unknown>): number | null {
  return asNumber(p.qty ?? p.quantity);
}

function positionValue(p: Record<string, unknown>): number | null {
  return asNumber(p.market_value ?? p.price ?? p.current_price);
}

/** Extract `{symbol -> {date}}` from the raw earnings DDB rows. */
function earningsBySymbol(earnings: unknown[]): Map<string, string | null> {
  const m = new Map<string, string | null>();
  for (const r of earnings) {
    if (!r || typeof r !== 'object') continue;
    const rec = r as Record<string, unknown>;
    let sym = upper(rec.symbol);
    if (!sym) {
      const key = rec.data_key;
      if (typeof key === 'string' && key.endsWith('#earnings')) {
        sym = upper(key.slice(0, -'#earnings'.length));
      }
    }
    if (!sym) continue;
    const date = typeof rec.next_earnings_date === 'string' ? rec.next_earnings_date : null;
    m.set(sym, date);
  }
  return m;
}

/** Calendar days from `now` (UTC date) to a YYYY-MM-DD earnings date; null when
 *  unparseable, negative when the date is already in the past. */
export function daysUntil(dateStr: string | null, now: Date): number | null {
  if (!dateStr) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
  if (!m) return null;
  const target = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.round((target - today) / 86_400_000);
}

export interface MergeInput {
  positions: unknown[];
  pool: unknown[];
  watchlist: unknown;
  earnings: unknown[];
  blackoutDays: number;
}

/**
 * Pure row-merge: union over symbols from positions ∪ pool ∪ watchlist, enriched
 * with earnings + blackout. Sorted by symbol for stable rendering/tests.
 *
 * Blackout is true when `0 <= daysUntil <= blackoutDays` — i.e. earnings land
 * today or within the forward window. (Past dates are not flagged; the agent
 * treats already-passed cached dates as stale, not as an active blackout.)
 */
export function mergeUniverse(input: MergeInput, now: Date): UniverseRow[] {
  const { positions, pool, watchlist, earnings, blackoutDays } = input;

  const poolBySymbol = new Map<string, string | null>();
  for (const p of pool) {
    if (!p || typeof p !== 'object') continue;
    const rec = p as Record<string, unknown>;
    const sym = upper(rec.symbol);
    if (sym) poolBySymbol.set(sym, typeof rec.sector === 'string' ? rec.sector : null);
  }

  const positionBySymbol = new Map<string, { qty: number | null; value: number | null }>();
  for (const p of positions) {
    const sym = positionSymbol(p);
    if (!sym) continue;
    const rec = p as Record<string, unknown>;
    positionBySymbol.set(sym, { qty: positionQty(rec), value: positionValue(rec) });
  }

  const wl = parseWatchlist(watchlist);
  const watchSymbols = new Set(wl.entries.map((e) => e.symbol));

  const earnings_ = earningsBySymbol(earnings);

  const allSymbols = new Set<string>([
    ...positionBySymbol.keys(),
    ...poolBySymbol.keys(),
    ...watchSymbols,
  ]);

  const rows: UniverseRow[] = [];
  for (const symbol of allSymbols) {
    const pos = positionBySymbol.get(symbol);
    const nextEarnings = earnings_.get(symbol) ?? null;
    const d = daysUntil(nextEarnings, now);
    const earningsBlackout = d !== null && d >= 0 && d <= blackoutDays;
    rows.push({
      symbol,
      sector: poolBySymbol.has(symbol) ? poolBySymbol.get(symbol)! : null,
      inPosition: positionBySymbol.has(symbol),
      inPool: poolBySymbol.has(symbol),
      inWatchlist: watchSymbols.has(symbol),
      qty: pos?.qty ?? null,
      lastValue: pos?.value ?? null,
      nextEarnings,
      earningsBlackout,
    });
  }

  rows.sort((a, b) => a.symbol.localeCompare(b.symbol));
  return rows;
}

/** The blackout-days threshold from settings, with registry-default fallback. */
export function blackoutDaysFromSettings(rows: SettingRow[] | undefined): number {
  const row = rows?.find((r) => r.key === 'research.earnings_blackout_days');
  const fromRow = asNumber(row?.value);
  if (fromRow !== null) return fromRow;
  const def = asNumber(byKey('research.earnings_blackout_days')?.default);
  return def ?? 7;
}

/** The current pool, normalized to `PoolEntry[]` for editing + the full-list PUT. */
export function normalizePool(pool: unknown[]): PoolEntry[] {
  const out: PoolEntry[] = [];
  for (const p of pool) {
    if (!p || typeof p !== 'object') continue;
    const rec = p as Record<string, unknown>;
    const sym = upper(rec.symbol);
    if (!sym) continue;
    out.push({ symbol: sym, sector: typeof rec.sector === 'string' ? rec.sector : null });
  }
  return out;
}

/** Render the watchlist as YAML text for the promote editor. The editor IS the
 *  YAML the store expects: we emit it as plain text the user edits directly,
 *  avoiding a runtime YAML dependency (see parseWatchlist note). */
export function watchlistToYaml(wl: ParsedWatchlist): string {
  const lines: string[] = [];
  if (wl.maxSize !== null) lines.push(`max_size: ${wl.maxSize}`);
  lines.push('watchlist:');
  for (const e of wl.entries) {
    lines.push(`  - symbol: ${e.symbol}`);
    if (typeof e.rationale === 'string' && e.rationale) {
      lines.push(`    rationale: ${yamlScalar(e.rationale)}`);
    }
    const added = (e as Record<string, unknown>).added;
    if (typeof added === 'string' && added) {
      lines.push(`    added: ${yamlScalar(added)}`);
    }
  }
  return lines.join('\n') + '\n';
}

/** Emit a YAML scalar, single-quoting (with internal `'` doubling) when the
 *  value contains a `:` or other YAML-significant characters so it survives the
 *  round-trip through parseWatchlistYamlish. Plain alphanumerics/dashes/dots
 *  (e.g. a symbol or an ISO date) are emitted bare. */
function yamlScalar(value: string): string {
  // A bare emit of a value with leading/trailing whitespace round-trips lossily
  // (parseWatchlistYamlish trims each scalar), so force quoting in that case.
  if (/^[A-Za-z0-9.\-/ ]+$/.test(value) && !/^[\s-]/.test(value) && !/^\s|\s$/.test(value)) {
    return value;
  }
  return `'${value.replace(/'/g, "''")}'`;
}

// ---------------------------------------------------------------------------
// Provenance badge.
// ---------------------------------------------------------------------------

function SourceBadge({ label, source }: { label: string; source: string }) {
  const live = source === 'live';
  return (
    <span
      className={`badge badge-source ${live ? 'badge-live' : 'badge-fallback'}`}
      data-source={source}
    >
      {label}: {live ? 'live' : `fallback (${source === 'fallback' ? 'repo' : source})`}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Watchlist promote editor — a CategoryEditor-style YAML-text flow.
// ---------------------------------------------------------------------------

interface WatchlistEditorProps {
  initialText: string;
  /** The artifact's own max_size ceiling (owner-controllable BY DESIGN — see
   *  the PR note below). Used for the ≤ max_size client-side check. */
  maxSize: number | null;
  /** The symbol being promoted — must be present in the edited text. */
  requireSymbol: string;
  busy: boolean;
  onCancel: () => void;
  onSave: (text: string) => void;
}

function WatchlistEditor({
  initialText,
  maxSize,
  requireSymbol,
  busy,
  onCancel,
  onSave,
}: WatchlistEditorProps) {
  const [text, setText] = useState(initialText);

  // Re-parse the edited text for the client-side guards. Both checks mirror the
  // intent of the server but are advisory here (the server is authoritative).
  const parsed = parseWatchlist(text);
  const count = parsed.entries.length;
  const overMax = maxSize !== null && count > maxSize;
  const hasSymbol = parsed.entries.some((e) => e.symbol === requireSymbol);

  const blockReason = overMax
    ? `Watchlist has ${count} entries — exceeds max_size ${maxSize}.`
    : !hasSymbol
      ? `${requireSymbol} is not present in the watchlist text.`
      : null;

  return (
    <div className="watchlist-editor" role="dialog" aria-modal="true" aria-label="Promote to watchlist">
      <h2 className="watchlist-editor-title">Promote {requireSymbol} to watchlist</h2>
      <p className="watchlist-editor-hint">
        Edit the watchlist artifact (YAML). The new entry has been prefilled.
      </p>
      <textarea
        className="watchlist-editor-text"
        aria-label="Watchlist YAML"
        value={text}
        rows={Math.max(8, text.split('\n').length + 1)}
        onChange={(e) => setText(e.target.value)}
        disabled={busy}
      />
      {blockReason && (
        <p className="error watchlist-editor-error" role="alert">
          {blockReason}
        </p>
      )}
      <div className="watchlist-editor-actions">
        <button type="button" className="btn btn-cancel" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-save"
          disabled={busy || blockReason !== null}
          onClick={() => onSave(text)}
        >
          Save watchlist
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add-to-pool input row.
// ---------------------------------------------------------------------------

function AddToPoolRow({
  busy,
  onAdd,
}: {
  busy: boolean;
  onAdd: (symbol: string, sector: string) => Promise<string | null>;
}) {
  const [symbol, setSymbol] = useState('');
  const [sector, setSector] = useState('');
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    // Await the PUT: only clear the inputs once it RESOLVES. A rejection (e.g.
    // an ApiError) surfaces inline here and the row keeps its values so the
    // owner can retry, rather than clearing optimistically on a fire-and-forget.
    const reason = await onAdd(symbol, sector);
    if (reason) {
      setErr(reason);
      return;
    }
    setSymbol('');
    setSector('');
    setErr(null);
  }

  return (
    <div className="pool-add-row">
      <input
        className="input pool-add-symbol"
        aria-label="Add pool symbol"
        placeholder="Symbol"
        value={symbol}
        onChange={(e) => setSymbol(e.target.value)}
        disabled={busy}
      />
      <input
        className="input pool-add-sector"
        aria-label="Add pool sector"
        placeholder="Sector"
        value={sector}
        onChange={(e) => setSector(e.target.value)}
        disabled={busy}
      />
      <button
        type="button"
        className="btn btn-pool-add"
        onClick={() => void submit()}
        disabled={busy || symbol.trim() === ''}
      >
        Add to pool
      </button>
      {err && (
        <p className="error pool-add-error" role="alert">
          {err}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The tab.
// ---------------------------------------------------------------------------

export default function UniverseTab() {
  const { canWrite } = useAuth();
  const { data, isLoading, isError, error } = useUniverse();
  const settings = useSettings();
  const putPool = usePutPool();
  const putContext = usePutContext();

  const [writeError, setWriteError] = useState<string | null>(null);
  // The promote editor target symbol (null = closed).
  const [promote, setPromote] = useState<string | null>(null);

  // Synchronous re-entrancy guards. `putPool.isPending` / `putContext.isPending`
  // only flip true asynchronously (after the mutation starts), so two same-tick
  // clicks both observe `false` and each fire a duplicate full-list PUT — and for
  // the add path the second PUT is built from the stale `pool`, silently dropping
  // the just-added symbol. The DECISION must be made on a ref whose `.current`
  // is set synchronously at the top of each write (before any await) and seen by
  // the second click immediately. The isPending flags still drive button styling.
  const poolSubmittingRef = useRef(false);
  const watchSubmittingRef = useRef(false);

  const blackoutDays = blackoutDaysFromSettings(settings.data?.settings);

  const rows = useMemo(() => {
    if (!data) return [];
    return mergeUniverse(
      {
        positions: data.positions ?? [],
        pool: data.pool ?? [],
        watchlist: data.watchlist,
        earnings: data.earnings ?? [],
        blackoutDays,
      },
      new Date(),
    );
  }, [data, blackoutDays]);

  if (isLoading) return <p className="universe-loading">Loading universe…</p>;
  if (isError) {
    return (
      <p className="universe-error error" role="alert">
        Failed to load universe: {error instanceof Error ? error.message : 'unknown error'}
      </p>
    );
  }

  const resp = data as UniverseResponse;
  const pool = normalizePool(resp.pool ?? []);
  const parsedWatch = parseWatchlist(resp.watchlist);

  // --- pool writes: edit a local copy, PUT the FULL new list (API replaces
  //     wholesale — see usePutPool / writes.ts). Returns an error string on
  //     failure (else null) so callers can surface it inline; the ref guard
  //     makes a same-tick second call a no-op. -------------------------------
  async function putFullPool(next: PoolEntry[]): Promise<string | null> {
    if (poolSubmittingRef.current) return null;
    poolSubmittingRef.current = true;
    setWriteError(null);
    try {
      await putPool.mutateAsync(next.map((e) => ({ symbol: e.symbol, sector: e.sector ?? null })));
      return null;
    } catch (e) {
      const msg =
        e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Pool write failed';
      setWriteError(msg);
      return msg;
    } finally {
      poolSubmittingRef.current = false;
    }
  }

  // Awaits the PUT and reports the outcome to AddToPoolRow: the inputs clear only
  // after the write RESOLVES, and a rejection's message lands inline (and in the
  // banner) instead of clearing optimistically on a fire-and-forget.
  async function onAddToPool(symbolRaw: string, sectorRaw: string): Promise<string | null> {
    const symbol = symbolRaw.trim().toUpperCase();
    const sector = sectorRaw.trim();
    if (!symbol) return 'Symbol is required.';
    if (pool.some((e) => e.symbol === symbol)) return `${symbol} is already in the pool.`;
    return putFullPool([...pool, { symbol, sector: sector || null }]);
  }

  function onRemoveFromPool(symbol: string) {
    void putFullPool(pool.filter((e) => e.symbol !== symbol));
  }

  // --- watchlist promote: prefill the YAML editor with the symbol appended. --
  function onPromoteToWatchlist(symbol: string) {
    setPromote(symbol);
  }

  function promoteInitialText(symbol: string): string {
    const exists = parsedWatch.entries.some((e) => e.symbol === symbol);
    const next: ParsedWatchlist = exists
      ? parsedWatch
      : {
          maxSize: parsedWatch.maxSize,
          entries: [
            ...parsedWatch.entries,
            { symbol, rationale: 'promoted from universe', added: new Date().toISOString().slice(0, 10) },
          ],
        };
    return watchlistToYaml(next);
  }

  async function onSaveWatchlist(text: string) {
    if (watchSubmittingRef.current) return;
    watchSubmittingRef.current = true;
    setWriteError(null);
    try {
      await putContext.mutateAsync({ artifact: 'watchlist', body: text });
      setPromote(null);
    } catch (e) {
      setWriteError(
        e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Watchlist write failed',
      );
    } finally {
      watchSubmittingRef.current = false;
    }
  }

  const positionsAsOf = resp.positions_as_of ?? 'unknown';
  const poolBusy = putPool.isPending;

  return (
    <section className="universe-tab" aria-label="Universe">
      <header className="universe-header">
        <span className="universe-asof">positions as of {positionsAsOf}</span>
        <SourceBadge label="pool" source={resp.pool_source} />
        <SourceBadge label="watchlist" source={resp.watchlist_source} />
      </header>

      {writeError && (
        <p className="error universe-write-error" role="alert">
          {writeError}
        </p>
      )}

      <UniverseMap rows={rows} />

      <SymbolTable
        rows={rows}
        canWrite={canWrite}
        busy={poolBusy}
        onRemoveFromPool={onRemoveFromPool}
        onPromoteToWatchlist={onPromoteToWatchlist}
      />

      {canWrite && <AddToPoolRow busy={poolBusy} onAdd={onAddToPool} />}

      {/* PRODUCT NOTE (flagged for PR review): the watchlist `max_size` is an
          owner-controllable ceiling that lives INSIDE the artifact value itself
          — editing the YAML can raise it. The ≤ max_size guard below is advisory
          (the server is authoritative); the owner can intentionally bump the
          ceiling. By design, per growth-spec 2026-06-04. */}
      {canWrite && promote !== null && (
        <WatchlistEditor
          initialText={promoteInitialText(promote)}
          maxSize={parsedWatch.maxSize}
          requireSymbol={promote}
          busy={putContext.isPending}
          onCancel={() => setPromote(null)}
          onSave={onSaveWatchlist}
        />
      )}
    </section>
  );
}
